import { Subscription } from 'zen-observable-ts';
import { SupportedEnvironments } from '@/config/environment';
import { NaluriChatError } from '@/errors';
import { invariant } from '@/utils/invariant';

import { AccountApi } from './api/account.api';
import { chatCache, ChatApi } from './api/chat.api';
import { eventsApi, EVENTS } from './api/events.api';
import { GatewayApi } from './api/gateway.api';
import { CoachListStatus } from './constants';
import { ChatChannel } from './core/chatChannel';
import { useFragment } from './generated';
import {
  ChatFieldsFragment,
  ChatFieldsFragmentDoc,
  ChatResultsFieldFragment,
  GetRoomsDocument,
  GetRoomsQuery,
  GetRoomsQueryVariables,
  NewMessageDocument,
  NewMessageSubscription,
  UserFieldsFragmentDoc,
} from './generated/graphql';
import messagesFragment from './lib/messageFragement';
import userFragment from './lib/userFragement';
import type {
  AccountUser,
  CoachProfile,
  NaluriChatLoginOptions,
  Channel,
  ChannelType,
  NaluriLogout,
  NewMessageData,
  SubscriptionCallback,
  CoachListFilters,
} from './types';

import { getGatewayEndpoint } from './utils/getGatewayEndpoint';
import {
  startRefreshTokenOnInterval,
  stopRefreshTokenOnInterval,
} from './utils/startRefreshTokenOnInterval';

import { tokenStore } from './utils/tokenStore';

/**
 * Naluri Chat Sdk constructor options
 */
export interface NaluriChatOptions {
  apiKey: string;
  apiSecret?: string;
  timeoutMs?: number;
  environment?: SupportedEnvironments;
  onRefreshTokenError?: () => Promise<void>;
}

/**
 * Naluri Chat Sdk
 * @constructor
 * @param {NaluriChatOptions} options
 */
class NaluriChat {
  /**
   * Api key of naluri chat
   */
  public readonly apiKey: string;

  /**
   * Secret key of naluri chat
   */
  public readonly apiSecret: string;

  /**
   * Environment, default to SIT
   */
  public readonly environment: SupportedEnvironments;

  /**
   * Function to be called when refresh token fails
   */
  public readonly onRefreshTokenError?: () => Promise<void>;

  /**
   * @private
   * Initialized chat instance
   */
  private static chatInstance?: NaluriChat;

  private accountInstance: AccountApi;

  private chatApiInstance?: ChatApi;

  private gatewayApiInstance?: GatewayApi;

  private chatChannelInstance?: ChatChannel;

  private senderId?: string;

  private subscription?: Subscription;

  private lastKeys = new Map<string, string[]>();

  /**
   * @private use getInstance method instead
   */
  private constructor({
    apiKey,
    apiSecret,
    environment,
    onRefreshTokenError,
  }: NaluriChatOptions) {
    /** @readonly */
    this.apiKey = apiKey;

    /** @readonly */
    this.apiSecret = apiSecret ?? '';

    this.environment = environment ?? SupportedEnvironments.SIT;

    if (onRefreshTokenError) this.onRefreshTokenError = onRefreshTokenError;

    this.accountInstance = new AccountApi({
      apiKey,
      apiUrl: getGatewayEndpoint(this.environment),
      onRefreshTokenError: this.onRefreshTokenError,
    });
  }

  /**
   * [IMPORTANT]: Initialise this first before using any other methods
   * Initialize a new Naluri Chat instance
   * @returns {NaluriChat}
   * @param {string} apikey
   * @throws {NaluriChatError}
   */
  public static initialize({
    apiKey,
    apiSecret,
    timeoutMs,
    environment,
    onRefreshTokenError,
  }: NaluriChatOptions): void {
    if (typeof apiKey !== 'string' || !apiKey) {
      throw new NaluriChatError({
        message: `Please initialize Naluri Chat with api key.`,
        code: '4001',
      });
    }

    if (this.chatInstance) {
      return;
    }

    const ChatInstance = new NaluriChat({
      apiKey,
      apiSecret,
      environment,
      timeoutMs,
      onRefreshTokenError,
    });

    this.chatInstance = ChatInstance;
  }

  /**
   * Gets a new Naluri Chat instance
   * @returns {NaluriChat}
   * @throws {NaluriChatError} If didn't intialize chat.
   */
  public static getInstance(): NaluriChat {
    if (!this.chatInstance) {
      throw new NaluriChatError({
        message: `Chat instance is not initialized. Please use the initialize method first.`,
        code: '4002',
      });
    }

    return this.chatInstance;
  }

  /**
   * Login user to chat
   * @param {NaluriChatLoginOptions} options
   * @returns {PromiseLike<AccountUser>}
   * @throws {NaluriChatError} if failed to login user
   */
  async login({
    authorizationCode,
  }: NaluriChatLoginOptions): Promise<AccountUser> {
    const { user } = await this.accountInstance.login(authorizationCode);
    if (!user) {
      if (eventsApi.isReady())
        eventsApi.track(EVENTS.LOGIN_ERROR, {
          error: 'Failed to login user',
        });

      throw new NaluriChatError({
        message: `Naluri couldn't login with this 'authorizationCode', Please contact admin.`,
        code: '4003',
      });
    }

    invariant(
      !!user.chatUserId,
      'Missing chatUserId from the API, please contact admin'
    );

    this.chatApiInstance = new ChatApi({
      accountApi: this.accountInstance,
      onRefreshTokenError: this.onRefreshTokenError,
    });

    this.senderId = user.chatUserId;
    this.gatewayApiInstance = new GatewayApi({
      apiUrl: getGatewayEndpoint(this.environment),
      apiKey: this.apiKey,
      accountApi: this.accountInstance,
      onRefreshTokenError: this.onRefreshTokenError,
    });

    if (this.environment !== SupportedEnvironments.DEV) {
      eventsApi.init(user.id, getGatewayEndpoint(this.environment));

      eventsApi.identify({
        // eslint-disable-next-line camelcase
        tenant_id: user.tenantId,
        country: user.country,
        language: user.preferredLanguage,
      });
    }

    await startRefreshTokenOnInterval(
      this.accountInstance,
      tokenStore,
      this.onRefreshTokenError
    );

    return user;
  }

  /**
   * Logout user from chat
   * @returns {PromiseLike<void>}
   * @throws NaluriChatError if failed to logout user
   */
  async logout(): Promise<NaluriLogout> {
    const refreshToken = tokenStore.getItem('refreshToken');
    if (eventsApi.isReady()) eventsApi.track(EVENTS.LOGOUT);

    try {
      await this.accountInstance.logout(refreshToken);
    } catch (error) {
      if (eventsApi.isReady()) eventsApi.track(EVENTS.LOGOUT_ERROR, { error });

      throw new NaluriChatError({
        message: 'Failed to logout user',
        code: '4004',
      });
    } finally {
      this.reset();
      tokenStore.clear();
      stopRefreshTokenOnInterval();
    }
    return { success: true };
  }

  /**
   * Gets chat channels
   * @returns {PromiseLike<Channel[]>}
   * @throws NaluriChatError if chat instance is not initialized
   */
  async getAllChannels({ userId }: { userId?: string } = {}): Promise<
    Channel[]
  > {
    if (!this.chatApiInstance) {
      throw new NaluriChatError({
        message:
          'Chat instance is not initialized. Please use the initialize method first.',
        code: '4002',
      });
    }

    if (eventsApi.isReady()) eventsApi.track(EVENTS.GET_CHANNELS);

    try {
      const { data } = await this.chatApiInstance.query<
        GetRoomsQuery,
        GetRoomsQueryVariables
      >({
        query: GetRoomsDocument,
        variables: {
          isChat: true,
          filter: userId,
        },
        fetchPolicy: 'network-only',
      });
      if (data?.rooms) {
        return data.rooms.map((room) => ({
          id: room.id,
          name: room.name,
          lastMessageId: room.last_chat_id ?? undefined,
          status: room.status ?? undefined,
          type: room.type as ChannelType,
          createdAt: room.created_at,
          updatedAt: room.updated_at,
          unreadCount: room.unread ?? 0,
          chat: room.last_chat
            ? messagesFragment(
                useFragment(ChatFieldsFragmentDoc, room.last_chat),
                this.senderId
              )
            : undefined,
          users: useFragment(UserFieldsFragmentDoc, room.users).map((user) =>
            userFragment(user)
          ),
          metadata: room.metadata?.length
            ? room.metadata?.map((meta) => ({
                key: meta.key,
                value: meta.value,
              }))
            : undefined,
        }));
      }
    } catch (err) {
      if (eventsApi.isReady())
        eventsApi.track(EVENTS.GET_CHANNELS_ERROR, { error: err });

      throw new NaluriChatError({
        message: 'Chat getAllChannels method error. ' + err,
        code: '4005',
      });
    }
    throw new NaluriChatError({
      message: 'Chat getAllChannels method error. ',
      code: '4005',
    });
  }

  /**
   * Gets a chat channel instance
   * @param {{ channelId: string }} channelOptions
   * @returns {ChatChannel}
   * @throws NaluriChatError if chat instance is not initialized
   */
  getChannel({ channelId }: { channelId: string }): ChatChannel {
    if (!this.chatApiInstance) {
      throw new NaluriChatError({
        message:
          'Chat instance is not initialized. Please use the initialize method first.',
        code: '4002',
      });
    }

    if (!this.senderId) {
      throw new NaluriChatError({
        message:
          'Sender id is not initialized. Please use the login method first.',
        code: '4002',
      });
    }

    if (!channelId) {
      throw new NaluriChatError({
        message: `"channelId" is requires. Please field in the channel id. Channel id provided: ${channelId}`,
        code: '4002',
      });
    }

    invariant(
      channelId,
      'ChannelId is requires. Please field in the channel id.'
    );

    this.chatChannelInstance = new ChatChannel({
      chatClient: this.chatApiInstance,
      gatewayClient: this.gatewayApiInstance,
      accountClient: this.accountInstance,
      eventsClient: eventsApi,
      senderId: this.senderId,
      roomId: channelId,
      lastKeys: this.lastKeys,
    });

    return this.chatChannelInstance;
  }

  /**
   * subscribe method to all channel
   * @param {SubscriptionCallback} params
   * @returns {Subscription}
   * @throws NaluriChatError if error
   */
  subscribe(onReceive: SubscriptionCallback): Subscription | undefined {
    if (!this.chatApiInstance || !chatCache) {
      throw new NaluriChatError({
        message:
          'Chat instance is not initialized. Please use the initialize method first.',
        code: '4002',
      });
    }
    this.subscription = this.chatApiInstance
      ?.subscribe<NewMessageSubscription>({
        query: NewMessageDocument,
      })
      .subscribe({
        next: (data: NewMessageData) => {
          const newMessage = useFragment(
            ChatFieldsFragmentDoc,
            data.data.newMessage
          );

          const rooms = chatCache.readQuery<
            GetRoomsQuery,
            GetRoomsQueryVariables
          >({
            query: GetRoomsDocument,
            variables: {
              isChat: true,
            },
          });

          const roomData = rooms?.rooms?.find(
            (room) => room.id === newMessage.room_id
          );

          const isMe = newMessage.sender_id === this.senderId;
          const currentUnread = roomData?.unread ?? 0;
          const unread = !isMe ? currentUnread + 1 : currentUnread;

          if (roomData) {
            chatCache.writeQuery<GetRoomsQuery, GetRoomsQueryVariables>({
              query: GetRoomsDocument,
              variables: {
                isChat: true,
              },
              data: {
                rooms: rooms?.rooms?.map((room) => {
                  if (room.id === newMessage.room_id) {
                    return {
                      ...room,
                      // eslint-disable-next-line camelcase
                      last_chat: newMessage,
                      // eslint-disable-next-line camelcase
                      last_chat_id: newMessage.id,
                      // eslint-disable-next-line camelcase
                      updated_at: newMessage.updated_at,
                      unread,
                    };
                  }
                  return room;
                }),
              },
            });
          } else {
            // message is not part of the chat channel rooms, do not fire callback
            return;
          }

          const newMessageRef = chatCache.writeFragment<ChatFieldsFragment>({
            id: `Chat:{"id": "${newMessage.id}"}`,
            fragment: {
              ...ChatFieldsFragmentDoc,
              definitions: [
                ...ChatFieldsFragmentDoc.definitions,
                ...UserFieldsFragmentDoc.definitions,
              ],
            },
            fragmentName: 'ChatFields',
            data: newMessage,
          });

          chatCache.modify({
            id: `ChatResults:{"room_id":"${newMessage.room_id}"}`,
            fields: {
              chats(
                existing: ChatResultsFieldFragment['chats'] = [],
                { readField }
              ) {
                const merged = existing ? [...existing] : [];

                if (
                  merged.find(
                    (ref) => readField<string>('id', ref) === newMessage.id
                  )
                ) {
                  return merged;
                }

                merged.push(newMessageRef as any);

                merged.sort((a, b) => {
                  const aCreatedAt = new Date(
                    readField<string>('created_at', a)
                  );
                  const bCreatedAt = new Date(
                    readField<string>('created_at', b)
                  );
                  return bCreatedAt.getTime() - aCreatedAt.getTime();
                });

                return merged;
              },
              count(existingCount = 0) {
                return existingCount + 1;
              },
            },
          });

          const returnNewMessage = messagesFragment(newMessage, this.senderId);

          const newRoom = {
            ...roomData,
            lastMessageId: returnNewMessage.id,
            // eslint-disable-next-line camelcase
            updatedAt: returnNewMessage.updatedAt,
            chat: returnNewMessage,
            unreadCount: unread,
          };

          delete newRoom.last_chat; // delete to avoid confusion
          delete newRoom.last_chat_id;
          delete newRoom.updated_at;
          delete newRoom.unread;

          onReceive({ data: newRoom, connected: true });
        },
        error(errorValue) {
          if (eventsApi.isReady())
            eventsApi.track(EVENTS.CHANNEL_LISTING_SUBSCRIPTION_ERROR, {
              error: errorValue,
            });

          onReceive({
            error: new NaluriChatError({
              message: `Channel list ${errorValue}`,
              code: '4006',
            }),
            connected: false,
          });
        },
        complete() {
          onReceive({ connected: false });
        },
        start() {
          onReceive({ connected: true });
        },
      });

    return this.subscription;
  }

  /**
   * Gets Coach Profile
   * @returns {PromiseLike<CoachProfile>}
   * @throws NaluriChatError if chat instance is not initialized
   */
  async getCoachProfile(coachId: string): Promise<CoachProfile> {
    if (!this.chatApiInstance || !this.gatewayApiInstance || !this.senderId) {
      throw new NaluriChatError({
        message:
          'Chat not initialized properly. Please use the login method first.',
        code: '4002',
      });
    }

    if (!coachId) {
      throw new NaluriChatError({
        message: `"coachId" is required. Please field in the coach id. Coach id provided: ${coachId}`,
        code: '4002',
      });
    }

    if (eventsApi.isReady()) eventsApi.track(EVENTS.GET_COACH_PROFILE);

    try {
      return (
        await this.gatewayApiInstance.getCoachList({
          coachId,
          status: CoachListStatus.ALL,
        })
      )[0];
    } catch (err) {
      if (eventsApi.isReady())
        eventsApi.track(EVENTS.GET_COACH_PROFILE_ERROR, { error: err });

      throw new NaluriChatError({
        message: `Chat getCoachProfile method error: ${err}`,
        code: '4008',
      });
    }
  }

  /**
   * Get coaches list details in the group channel.
   * @returns {PromiseLike<CoachProfile[]>}
   * @throws NaluriChatError if chat instance is not initialized
   */
  async getCoachList(filters?: CoachListFilters): Promise<CoachProfile[]> {
    if (!this.gatewayApiInstance) {
      throw new NaluriChatError({
        message:
          'Chat not initialized properly. Please use the login method first.',
        code: '4002',
      });
    }

    if (eventsApi.isReady()) eventsApi.track(EVENTS.GET_COACHES);

    try {
      return await this.gatewayApiInstance.getCoachList(filters);
    } catch (err) {
      if (eventsApi.isReady())
        eventsApi.track(EVENTS.GET_COACHES_ERROR, { error: err });

      throw new NaluriChatError({
        message: 'Chat getCoachList method error. ' + err,
        code: '4011',
      });
    }
  }

  private reset() {
    chatCache?.reset({
      discardWatches: true,
    });

    this.senderId = undefined;
    this.chatApiInstance = undefined;
    this.gatewayApiInstance = undefined;

    stopRefreshTokenOnInterval();

    if (eventsApi.isReady()) eventsApi.cleanup();

    tokenStore.clear();
  }

  destroy() {
    this.reset();
    NaluriChat.chatInstance = undefined;
  }
}

export default NaluriChat;
